import { clsx, styx } from "modules/Utils";
import { CSSProperties, Fragment } from "react";
import SwitchSymphony from "./SwitchSymphony";
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import Icon from "./Icon";

export interface OptionItem {
    id: string | number;
    icon?: string;
    iconRight?: string;
    label?: string;
    unavailable?: boolean;
    description?: string;
    availabilityNote?: string;
    childrenLeft?: React.ReactNode;
    childrenRight?: React.ReactNode;
    isPaid?: boolean;
    [key: string]: any;
}

interface OptionsSymphonyProps {
    className: string;
    style?: CSSProperties;
    styleOptionRow?: CSSProperties;
    options: OptionItem[];
    value: string | number;
    handleOptionSelected: (value: string | number) => void;
    size?: 'large' | 'small';
    isRadio?: boolean;
    iconColor?: string;
    childrenLeftOffset?: number;
}

const OptionsSymphony = ({
    className,
    style,
    styleOptionRow,
    options,
    value,
    handleOptionSelected: handleOptionSelectedProp,
    size = 'large',
    isRadio = true,
    iconColor = Colors.black,
    childrenLeftOffset = 0,
}: OptionsSymphonyProps) => {
    const handleOptionSelected = (option: OptionItem) => {
        const { id, unavailable } = option;
        if (unavailable) {
            return
        }

        handleOptionSelectedProp(id);
    }

    const renderOptionRow = (option: OptionItem) => {
        const {
            id,
            icon,
            iconRight,
            label,
            unavailable,
            description,
            availabilityNote,
            childrenLeft,
            childrenRight,
            isPaid,
        } = option;

        const isSelected = id === value;

        return (
            <div
                className="optionRow"
                style={styx(
                    sty.optionRow,
                    unavailable && sty.optionRowUnavailable,
                    isSelected && sty.optionRowSelected,
                    sty[`optionRow_${size}`],
                    styleOptionRow
                )}
                onClick={() => handleOptionSelected(option)}
            >
                {isRadio &&
                    <SwitchSymphony
                        className="optionRowSwitch"
                        checked={isSelected}
                        type="radio"
                    />
                }
                {icon &&
                    <Icon
                        className="optionIcon"
                        icon={icon}
                        size={40}
                        color={isSelected ? (size === 'small' ? Colors.purple : Colors.grey) : iconColor}
                    />
                }
                {childrenLeft &&
                    <div style={{ marginRight: childrenLeftOffset }}>
                        {childrenLeft}
                    </div>
                }
                <div
                    className="optionContent"
                    style={styx(
                        { display: 'flex', flexDirection: 'column', gap: 4 },
                        Sty.text16bold,
                        !isSelected && size === 'small' && Sty.text16grey,
                        isSelected && size === 'small' && Sty.text16purple
                    )}
                >
                    <div
                        className="optionLabel"
                        style={styx(
                            size === 'small' && {
                                whiteSpace: 'nowrap',
                            }
                        )}
                    >
                        {label}
                    </div>
                    <div className="optionDescription" style={Sty.text16grey}>
                        {description}
                    </div>

                    {availabilityNote &&
                        <div className="optionAvailabilityNote" style={Sty.text16grey}>
                            {availabilityNote}
                        </div>
                    }
                </div>

                {iconRight &&
                    <Icon
                        className="optionProBadge"
                        icon={iconRight}
                        size={20}
                        color={Colors.purple}
                    />
                }

                {childrenRight &&
                    childrenRight
                }

                {isPaid &&
                    <Icon
                        className="optionProBadge"
                        icon="gem"
                        size={20}
                    />
                }
            </div>
        )
    }

    return (
        <div
            className={clsx('optionsSymphonyElement', className)}
            style={styx(
                sty.optionsSymphonyElement,
                sty[`optionsSymphonyElement_${size}`],
                style
            )}
        >
            {options.map((option) => (
                <Fragment key={option.id}>
                    {renderOptionRow(option)}
                </Fragment>
            ))}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    optionsSymphonyElement: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%'
    },
    optionsSymphonyElement_small: {
        gap: 8,
        flexDirection: 'row',
    },
    optionRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        alignItems: 'center',
        borderColor: Colors.greyLighter,
        borderWidth: 1,
        borderRadius: 16,
        padding: 16,
        cursor: 'pointer',
        width: '100%'
    },
    optionRow_small: {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 8,
        width: 'auto',
        gap: 8,
    },
    optionRowUnavailable: {
        opacity: 0.5,
        cursor: 'not-allowed'
    },
    optionRowSelected: {
        backgroundColor: Colors.purpleLighter,
        borderColor: Colors.purple
    }
}

export default OptionsSymphony;